/**
 * Pure pruning helper for context-scoped filter selections.
 *
 * Persisted filters (tracks, sectors, protection modes, teams…) reference ids
 * that belong to a specific project/network/client context. When the active
 * context changes — or after rehydration from localStorage — ids that no longer
 * exist must be dropped, otherwise they act as ghost filters that hide
 * everything. Each filter component owns the list of its valid options (it
 * fetches them), so it self-heals its own selection with this helper.
 */

/**
 * Keep `selected` ids that exist in `valid`, preserving `selected`'s order.
 * Returns the SAME reference when nothing is dropped, so callers can guard with
 * a `result !== selected` check and avoid emitting a no-op update that would
 * re-render every consumer (or loop in an effect).
 */
export function pruneIds(selected: string[], valid: string[]): string[] {
  const validSet = new Set(valid)
  const next = selected.filter(id => validSet.has(id))
  return next.length === selected.length ? selected : next
}

/**
 * Keep only the notions attached to a visible track.
 *
 * The geo frieze draws its bands (protection zones, geo markers, accesses,
 * stations) once and flat, so — unlike the per-lane linear rows — nothing
 * narrows them to the work-view track filter. A zone on V3/V7 would keep
 * showing while only V1/V2 lanes are displayed. Each notion arrives with its
 * `trackIds` already resolved to network track ids (the same ids `workTrackIds`
 * holds), so a set intersection is the whole test.
 *
 * An empty selection means "all tracks" (the filter is off): return the input
 * reference untouched so callers memoise cheaply.
 */
export function filterByVisibleTracks<T extends {trackIds?: string[]}>(
  items: T[],
  visibleTrackIds: string[],
): T[] {
  if (visibleTrackIds.length === 0) return items
  const visible = new Set(visibleTrackIds)
  return items.filter(item => item.trackIds?.some(id => visible.has(id)))
}
